import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { getRoleMenuItemSx, RoleMenuItem } from "@components/role/role-menu-item";
import { ChangeRoleEntityType, useChangeRoleBulkDialogContent } from "@components/table/members/bulk-actions/bulk-change-role-helper";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { AssignableUpdateCompanyMemberRole } from "@custom-types/member-types";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector, selectedEntitiesSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, updateMany, updateOne } from "@store/table/table-slice";
import { currentUserSelector } from "@store/user/user-selector";
import { formatUserRoleType } from "@utils/data-display";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { isXgSeatRequiredForRole } from "@components/table/members/assign-seats/use-is-xg-seat-required-for-role";
import { useBulkRoleXgSeats } from "@components/table/members/bulk-actions/use-bulk-role-xg-seats";
import { useMemberSeatsLookup } from "@hooks/use-member-seats-lookup";
import { fetchingSdbCompanyFlagsSelector } from "@store/sdb-company/sdb-company-selector";
import { getAssignedWorkspaceRole } from "@utils/workspace-role-utils";
import { doesWorkspaceRoleRequireXgSeat, getTeamXgSeatBlockedRoleMessage } from "@utils/xg-seat-utils";
import { useEffect, useState } from "react";
import { Text, Tooltip, UserRoleIcon, MenuItem } from "@flat-ui";
/**
 * Bulk-assignable workspace roles (Group Viewer is always excluded upstream in
 * `AssignableUpdateCompanyMemberRole`). Admin can only be assigned when the
 * workspace can assign managers. "No workspace role" is offered for teams, and
 * also for members when managers can't be assigned (Scan Essential and below),
 * where every member is a viewer that can be safely downgraded.
 */
function getBulkAssignableCompanyRoles(entityType, canAssignManagers) {
  const canAssignNoRole = entityType === "teams" || !canAssignManagers;
  return AssignableUpdateCompanyMemberRole.filter(role => {
    if (role === "COMPANY_EXECUTIVE") {
      return canAssignManagers;
    }
    if (role === "MEMBER") {
      return canAssignNoRole;
    }
    return true;
  });
}

/** Why a role that needs a Sphere XG seat cannot be picked while the plan is loading */
const PLAN_LOADING_REASON = "The Sphere XG seats of the workspace are still loading.";

/**
 * Why a role that needs a Sphere XG seat cannot be picked while the plan of the workspace is not
 * known, since it is not known then whether the members need a seat for it
 */
const UNKNOWN_PLAN_REASONS = {
  uninitialized: PLAN_LOADING_REASON,
  pending: PLAN_LOADING_REASON,
  rejected: "The Sphere XG seats of the workspace could not be loaded. Please reload the page."
};

/** Button allowing to change role of members or teams in company */
export function BulkChangeCompanyRole({
  companyId,
  entityType
}) {
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [newRole, setNewRole] = useState("MEMBER");
  const currentUser = useAppSelector(currentUserSelector);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  const selectedEntities = useAppSelector(selectedEntitiesSelector(entityType));
  const mode = useBulkActionsMode();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const memberSeatsLookup = useMemberSeatsLookup();
  const {
    fetchCompanySubscriptions
  } = useAppSelector(fetchingSdbCompanyFlagsSelector);
  const dispatch = useAppDispatch();
  const {
    getItemNote,
    alert,
    footer,
    hasSeatShortage,
    changeRoleWithXgSeat
  } = useBulkRoleXgSeats({
    // Teams cannot hold a seat, and the roles that need one are not offered to them
    selectedEntities: entityType === "members" ? selectedEntities : [],
    // A member that holds the role only through a team or group still needs the seat
    isXgSeatRequired: member => getAssignedWorkspaceRole(member) !== newRole && isXgSeatRequiredForRole(member, newRole, memberSeatsLookup)
  });
  const {
    bulkDialogContent,
    successDialogContent
  } = useChangeRoleBulkDialogContent({
    subjectType: "workspace",
    newRole,
    entityType: entityType === "members" ? ChangeRoleEntityType.member : ChangeRoleEntityType.team
  });
  const assignableRoles = getBulkAssignableCompanyRoles(entityType, canAssignManagers);

  // Closes the bulk dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);
  function onNewRoleClicked(event) {
    dispatch(setBulkActionName("changeRole"));
    const notAllowedChanges = [];
    for (const entity of selectedEntities) {
      // Marking entities as not allowed if project and group managers are selected for changing role to member
      if (event.currentTarget.dataset.myValue === "MEMBER" && (entity.role === "COMPANY_MANAGER" || entity.role === "PROJECT_MANAGER")) {
        notAllowedChanges.push({
          id: entity.identity,
          changes: {
            status: "not-allowed",
            message: `You cannot change from ${formatUserRoleType(entity.role)} role to ${formatUserRoleType("MEMBER")}.`
          }
        });
      }
    }
    if (notAllowedChanges.length) {
      dispatch(updateMany(notAllowedChanges));
    }
    if (currentUser) {
      dispatch(updateOne({
        id: currentUser.identity,
        changes: {
          status: "not-allowed",
          message: "You cannot change the role of yourself."
        }
      }));
    }

    // TODO: Check whether it is possible to remove type-casting: https://faro01.atlassian.net/browse/ST-1348
    setNewRole(event.currentTarget.dataset.myValue);
    setShouldShowDialog(true);
  }
  const config = getBulkActionButtonConfig(mode, "Change role in workspace");
  return <>
      <FaroTextMenuButton buttonTitle={config.shouldShowText ? <Text variant="buttonM">Change role</Text> : undefined} tooltipText={config.tooltipText} buttonSx={() => config.sx} menuPosition={{
      anchorOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "bottom",
        horizontal: "left"
      }
    }} rippleColor={config.rippleColor} shouldShowEndIcon={false} startIcon={<UserRoleIcon sx={{
      width: "18px",
      height: "18px"
    }} />}>
        {assignableRoles.map(role => {
        const disabledReason = getRoleDisabledReason(role);

        // The item stays a direct child of the menu, so that the menu skips it when it is
        // disabled, and only its content tells why, which needs the pointer to reach it
        return <MenuItem data-my-value={role} key={role} value={role} onClick={disabledReason ? undefined : onNewRoleClicked} disabled={!!disabledReason} sx={{
          ...getRoleMenuItemSx({
            isTableCell: false
          }),
          "&.Mui-disabled": {
            pointerEvents: "auto"
          }
        }}>
              {disabledReason ?
          // Describes the role rather than naming it, so that the item is still read as the role
          <Tooltip component="div" title={disabledReason} describeChild>
                  <RoleMenuItem role={role} />
                </Tooltip> : <RoleMenuItem role={role} />}
            </MenuItem>;
      })}
      </FaroTextMenuButton>

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "changeRole" && shouldShowSuccessDialog && <BulkSuccessDialog dialogContent={successDialogContent} />}

      {bulkActionName === "changeRole" && shouldShowDialog && <FaroBulkDialog dialogContent={bulkDialogContent} onClose={() => setShouldShowDialog(false)} trackingEvent={BulkActionEvents.bulkChangeRole} shouldDisableConfirmButton={hasSeatShortage} bulkActionCallback={member_0 => changeRoleWithXgSeat(member_0, () => dispatch(updateMemberRoleInWorkspace({
      companyId,
      role: newRole,
      identity: member_0.identity
    })).unwrap())} selectedEntities={selectedEntities} uniqueIdKey="identity" tableDataType={entityType} getItemNote={getItemNote} footer={footer}>
          {alert}
        </FaroBulkDialog>}
    </>;

  /**
   * Returns why the role cannot be picked, or undefined when it can. A role that needs a Sphere XG
   * seat waits for the plan to be known, so that the seats are never skipped for not knowing it,
   * and is not offered to teams on a plan that limits the seats, since teams cannot hold one.
   */
  function getRoleDisabledReason(role_0) {
    if (!doesWorkspaceRoleRequireXgSeat(role_0)) {
      return undefined;
    }
    const unknownPlanReason = UNKNOWN_PLAN_REASONS[fetchCompanySubscriptions];
    if (unknownPlanReason) {
      return unknownPlanReason;
    }
    return entityType === "teams" && memberSeatsLookup.isActiveSeatPlan ? getTeamXgSeatBlockedRoleMessage(role_0) : undefined;
  }
}